// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ExpandLess, ExpandMore } from '@/constants/hardfin-standard-icons';
import { InventoryDimension } from '@/inventory/hooks';
import { InventoryRowActions } from '@/items/components/inventory-row-actions';
import { SparseItem, SparseLocation } from '@/sparse/api/types';
import { formatNumber } from '@/utilities/number';
import {
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { PropsWithChildren } from 'react';

interface InventoryHeaderRowProps {
  view: InventoryDimension;
  expanded: boolean;
  onToggleExpand: () => void;
  hasChildren: boolean;
  name: string;
  secondaryText?: string;
  totalQuantity: number;
  item?: SparseItem;
  location?: SparseLocation;
  hideActions?: boolean;
}

/**
 * Renders a collapsible header row for inventory items.
 */
export function InventoryHeaderRow(
  props: PropsWithChildren<InventoryHeaderRowProps>
) {
  const {
    view,
    expanded,
    onToggleExpand,
    hasChildren,
    name,
    secondaryText,
    totalQuantity,
  } = props;

  return (
    <>
      <TableRow>
        <TableCell width="55%">
          <Stack direction="row" alignItems="center" spacing={1}>
            {hasChildren && (
              <IconButton
                onClick={onToggleExpand}
                size="small"
                aria-label={expanded ? 'Collapse' : 'Expand'}
              >
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}

            {view === 'item' ? (
              <Stack>
                <Typography variant="body2">{name}</Typography>
                {secondaryText && (
                  <Typography variant="body2" color="text.secondary">
                    {secondaryText}
                  </Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2">{name}</Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell width="20%" align="right">
          <Typography variant="body2">{formatNumber(totalQuantity)}</Typography>
        </TableCell>
        <TableCell width="25%" align="right">
          {!props.hideActions && (
            <InventoryRowActions
              item={props.view === 'item' ? props.item : undefined}
              location={props.view === 'location' ? props.location : undefined}
            />
          )}
        </TableCell>
      </TableRow>
      {props.children}
    </>
  );
}
